import { z } from 'zod';

import { BaseFlowBlockSchema, BlockType } from '../base/baseSchema.js';
import {
  METHOD_AND_ENDPOINT_HELPER_TEXT,
  RequestBodyFormat,
  SecureHeaderSchema,
  extractDomain,
} from '../rest/config.js';

export const RestApiClassicAuthorizationBlockConfigSchema = z
  .object({
    endpoint: z.string().min(4, {
      message: `Required - ${METHOD_AND_ENDPOINT_HELPER_TEXT}`,
    }),
    apiInstanceId: z.string(),
    method: z.enum(['GET', 'POST', 'PUT', 'PATCH', 'DELETE']),
    secureHeaders: z.array(SecureHeaderSchema).optional(),
    selectedResponseHeaders: z.array(z.string()).optional(),
    headers: z
      .array(
        z.object({
          key: z.string().min(1, {
            message: 'Header key cannot be empty.',
          }),
          value: z.string().min(1, {
            message: 'Header value cannot be empty.',
          }),
        })
      )
      .refine(
        (headers) =>
          new Set(headers.map((header) => `${header.key}-${header.value}`)).size === headers.length,
        'Headers cannot have duplicates'
      )
      .optional(),
    bodyFormat: z.nativeEnum(RequestBodyFormat).optional().default(RequestBodyFormat.RawJson),
    params: z.string().optional(),
    body: z.string().optional(),
  })
  .refine(
    ({ secureHeaders, endpoint }) => {
      if (!secureHeaders) {
        return true;
      }
      const currentDomain = extractDomain(endpoint);
      return secureHeaders.every((header) => {
        return !header.domain || header.domain === currentDomain;
      });
    },
    {
      message: 'One or more secure headers have a domain that does not match the endpoint domain.',
      path: ['secureHeaders'],
    }
  );

export const RestApiClassicAuthorizationBlockSchema = BaseFlowBlockSchema.extend({
  type: z.literal(BlockType.RestApiClassicAuthorization),
  config: RestApiClassicAuthorizationBlockConfigSchema,
});

export type RestApiClassicAuthorization = z.infer<typeof RestApiClassicAuthorizationBlockSchema>;
